import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { SignaturePlaceholder } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { useDebounceCallback } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import SignatureCanvas from 'react-signature-canvas';
import { useRect } from 'react-use-rect';
import pen2x from './icons/pen@2x.png';
import pen from './icons/pen.png';
import { SignatureClearButton } from './signature-clear-button';
import {
  blobToFile,
  exportSignatureAsBlackAndWhite,
  signatureImport
} from './utils';

type SignatureProps = {
  id: string;
  fieldColor: string;
  fieldBorderColor: string;
  fieldFillColor: string;
  fieldBorderRadius: string;
  fieldBorderWidth: string;
  fieldActiveColor: string;
  fieldBorderType?: 'outline' | 'underline' | null;
  value?: [File];
  layout?: 'vertical' | 'horizontal';
  onChange: (file?: [File]) => void;
};

export const Signature = ({
  id,
  value,
  fieldColor,
  fieldBorderColor,
  fieldFillColor,
  fieldBorderRadius,
  fieldBorderWidth,
  fieldBorderType,
  fieldActiveColor,
  layout = 'vertical',
  onChange
}: SignatureProps) => {
  const [canvasWidth, setCanvasWidth] = useState(200);
  const [isHovered, setIsHovered] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  const canvasRef = useRef<InstanceType<typeof SignatureCanvas> | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const [ref] = useRect(({ width }) => setCanvasWidth(width), { resize: true });
  const mergedContainerRef = useMergedRef(ref, containerRef);

  const canvasHeight = layout === 'horizontal' ? 100 : 200;

  const clear = useCallback(() => {
    canvasRef.current?.clear();
    setIsEmpty(true);
    onChange(undefined);
  }, [onChange]);

  const processSignature = useCallback(async () => {
    const sourceCanvas = canvasRef.current?.getCanvas();

    if (!sourceCanvas || canvasRef.current?.isEmpty()) {
      return;
    }

    try {
      const blob = await exportSignatureAsBlackAndWhite(sourceCanvas);

      if (!blob) {
        throw new Error('Failed to export signature as blob');
      }
      const file = blobToFile(blob);

      onChange([file]);
      setIsEmpty(false);
    } catch (error) {
      console.error('Error processing signature:', error);
    }
  }, [onChange]);

  const debouncedProcessSignature = useDebounceCallback(processSignature);

  const handleSignatureBegin = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.focus();
    }

    setIsDrawing(true);
    setIsEmpty(false);
  }, []);

  const handleSignatureEnd = useCallback(() => {
    debouncedProcessSignature();
    setIsDrawing(false);
  }, [debouncedProcessSignature]);

  const handleSignatureMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleSignatureMouseLeave = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.blur();
    }

    setIsHovered(false);
  }, []);

  useEffect(() => {
    if (!value?.length) {
      return;
    }

    const file = value[0] instanceof File ? value[0] : null;
    if (!file) {
      return;
    }

    const sigCanvas = canvasRef.current;
    if (!sigCanvas) {
      return;
    }

    const importSignature = async () => {
      try {
        await signatureImport(file, sigCanvas, fieldColor);
        setIsEmpty(false);
      } catch (err) {
        console.error('Signature import failed:', err);
      }
    };

    importSignature();
  }, [fieldColor, canvasWidth]);

  return (
    <Container
      ref={mergedContainerRef}
      onContextMenu={(e) => e.preventDefault()}
      onMouseEnter={handleSignatureMouseEnter}
      onMouseLeave={handleSignatureMouseLeave}
      tabIndex={0}
      aria-label="Signature field"
      aria-labelledby={id}
      _fieldBorderRadius={fieldBorderRadius}
      _fieldBorderWidth={fieldBorderWidth}
      _withBorder={!!fieldBorderType}
      _fieldBorderColor={fieldBorderColor}
      _fieldColor={fieldColor}
      _fieldFillColor={fieldFillColor}
      _fieldActiveColor={fieldActiveColor || getColorWithAlpha(fieldColor, 0.2)}
      _fieldHeight={canvasHeight}
    >
      <SignatureCanvas
        ref={canvasRef}
        minDistance={2}
        penColor={fieldColor}
        clearOnResize={false}
        canvasProps={{
          className: createReadableClass({
            component: 'signature',
            element: 'canvas'
          }),
          height: canvasHeight,
          width: canvasWidth
        }}
        minWidth={2}
        maxWidth={2}
        onBegin={handleSignatureBegin}
        onEnd={handleSignatureEnd}
      />

      <SignatureClearButton
        color={fieldColor}
        onClick={clear}
        isShowButton={isHovered || !isEmpty}
        isDrawing={isDrawing}
        fieldBorderRadius={fieldBorderRadius}
      />

      <StyledIcon
        component={SignaturePlaceholder}
        fill={fieldColor}
        size={[canvasWidth - 24, canvasHeight - 24]}
        _isTransparent={isEmpty && !isHovered}
      />
    </Container>
  );
};

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'signature',
    element: 'container'
  })
})<{
  _fieldBorderRadius: string;
  _fieldBorderWidth: string;
  _withBorder: boolean;
  _fieldColor: string;
  _fieldActiveColor: string;
  _fieldFillColor: string;
  _fieldBorderColor: string;
  _fieldHeight: number;
}>`
  cursor:
    image-set(url(${pen}) 1x, url(${pen2x}) 2x) 2 2,
    auto;
  height: ${({ _fieldHeight }) => `${_fieldHeight}px`};
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
  border-radius: ${({ _fieldBorderRadius }) => `${_fieldBorderRadius}px`};
  border-width: ${({ _fieldBorderWidth }) => `${_fieldBorderWidth}px`};
  border-style: solid;
  border-color: ${({ _fieldBorderColor, _withBorder }) =>
    _withBorder ? _fieldBorderColor : 'transparent'};
  background-color: ${({ _fieldFillColor }) => _fieldFillColor};
  color: ${({ _fieldColor }) => _fieldColor};
  box-sizing: border-box;
  transition: border-color 0.3s ease;

  &:hover,
  &:focus {
    border-color: ${({ _fieldActiveColor }) => _fieldActiveColor};
    outline: none;
  }
`;

const StyledIcon = styled(Icon).attrs({
  className: createReadableClass({
    component: 'signature',
    element: 'icon'
  })
})<{ _isTransparent?: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  padding: 12px;
  opacity: ${({ _isTransparent }) => (_isTransparent ? 0.1 : 0)};
  transition: opacity 0.3s ease;

  & svg {
    max-height: 130px;
    max-width: 140px;
    fill-opacity: 1;
  }

  & path {
    fill-opacity: 1;
  }
`;
